import React from 'react'

interface SkeletonProps {
  /** Custom CSS class name appended to the base class */
  className?: string
  /** Width — any CSS value (e.g. '100%', '80px', 200). Numbers are treated as px. */
  width?: string | number
  /** Height — any CSS value. Numbers are treated as px. */
  height?: string | number
  /** Border-radius — any CSS value. Numbers are treated as px. Defaults to 4px. */
  borderRadius?: string | number
  /** Render a circle (sets border-radius: 50% and equal width/height) */
  circle?: boolean
}

function toCss (value: string | number): string {
  return typeof value === 'number' ? `${value}px` : value
}

/**
 * Universal skeleton placeholder with shimmer animation.
 *
 * Uses the project's `background-gray-placeholder` mixin for consistent
 * shimmer styling across the app (same as TilePlaceholder, BannerPlaceholder, etc.)
 *
 * @example
 * ```tsx
 * // Text line
 * <Skeleton width="100%" height={15} />
 *
 * // Circle avatar
 * <Skeleton circle width={40} height={40} />
 *
 * // Pill button
 * <Skeleton width={334} height={44} borderRadius={26} />
 * ```
 */
export default function Skeleton ({
  className,
  width,
  height,
  borderRadius,
  circle = false,
}: SkeletonProps): React.ReactElement {
  const style: React.CSSProperties = {}

  if (width !== undefined) style.width = toCss(width)
  if (height !== undefined) style.height = toCss(height)

  if (circle) {
    style.borderRadius = '50%'
  } else if (borderRadius !== undefined) {
    style.borderRadius = toCss(borderRadius)
  }

  const baseClass = 'skeleton'
  const classes = className ? `${baseClass} ${className}` : baseClass

  return <div className={classes} style={style} aria-hidden="true" />
}
